import { UserModel } from './user.model';
import { ShiftModel } from './shift.model';

export type RegimeCycleType = 'ciclo' | 'semanal';

export interface RegimeModel {
    regime_id?: number;
    company_id: number;
    name: string;
    /** Horário de início do turno (HH:mm). Fim calculado por work_hours. */
    start_time?: string | null;
    work_hours: number;
    off_hours: number;
    cycle_type: RegimeCycleType;
    days_on: number;
    days_off: number;
    monthly_load: number;
    /** Posto coberto 24h em revezamento: o dia vira 24/work_hours turnos. */
    covers_24h?: boolean;
    /** Períodos persistidos (diurno/noturno…). */
    periods?: RegimePeriodModel[];
}

export interface RegimePeriodModel {
    regime_period_id?: number;
    regime_id?: number;
    slot?: number;
    name?: string | null;
    /** Sigla na matriz (A, B, P12…). */
    sigla?: string | null;
    color?: string | null;
    min_staff?: number | null;
    start_time?: string;
    end_time?: string;
}

export interface ShiftTemplateModel {
    shift_template_id?: number;
    company_id: number;
    code: string;
    name?: string | null;
    start_time: string;
    end_time: string;
    duration_min?: number;
    color?: string | null;
    /** Mínimo por função no posto: { DRIVER: 1, NURSE: 1 }. */
    min_by_role?: Record<string, number> | null;
}

export type ScheduleStatus =
    | 'ESCALADO'
    | 'FOLGA'
    | 'FALTA'
    | 'ATESTADO'
    | 'FERIAS'
    | 'LICENCA'
    | 'EXTRA'
    | 'TREINAMENTO';

export interface ScheduleEntryModel {
    schedule_entry_id?: number;
    company_id: number;
    user_id: number;
    /** yyyy-MM-dd */
    date: string;
    shift_id?: number | null;
    shift_template_id?: number | null;
    shift_period_id?: number | null;
    regime_id?: number | null;
    regime_slot?: number | null;
    regime_period_id?: number | null;
    status: ScheduleStatus;
    obs?: string | null;
    user?: UserModel | null;
    shift?: ShiftModel | null;
    shift_template?: ShiftTemplateModel | null;
    regime?: RegimeModel | null;
}

export interface CoverageGap {
    date: string;
    shift_id: number | null;
    shift_name: string | null;
    shift_template_id: number | null;
    role: string;
    expected: number;
    present: number;
}

export interface TeamMember {
    user_id: number;
    role: string;
    name: string | null;
    /** Regime de escala do colaborador (herdado do cadastro dele). */
    regime_id?: number | null;
}

export interface ScheduleTeam {
    shift_id: number;
    name: string | null;
    group_type: 'LOCAL' | 'REMOTE';
    vehicle_id: number | null;
    /** Nome da viatura (ex.: VTR-06), quando disponível na API. */
    vehicle_name?: string | null;
    /** Funções habilitadas neste grupo, escolhidas antes de montar o elenco. */
    enabled_roles?: string[];
    members: TeamMember[];
}

export interface ScheduleMonthResponse {
    entries: ScheduleEntryModel[];
    coverage_gaps: CoverageGap[];
    teams?: ScheduleTeam[];
    regimes?: RegimeModel[];
}

/** Situações que contam como presença na aba Presenças / cobertura do posto. */
export const PRESENT_STATUSES: ScheduleStatus[] = ['ESCALADO', 'EXTRA'];

/**
 * Contam na carga mensal (Xh / Yh e “a cobrir”). Atestado é hora de trabalho
 * contratual; não entra em cobertura do posto (use PRESENT_STATUSES).
 */
export const MONTHLY_LOAD_STATUSES: ScheduleStatus[] = ['ESCALADO', 'EXTRA', 'ATESTADO'];

/** Motivos selecionáveis na aba Ausências. */
export const ABSENCE_TAB_STATUSES: ScheduleStatus[] = [
    'FOLGA',
    'FALTA',
    'ATESTADO',
    'FERIAS',
    'LICENCA',
    'TREINAMENTO',
];

/** Metadados de exibição de cada status (cor, sigla, se é ausência). */
export const SCHEDULE_STATUS_META: Record<
    ScheduleStatus,
    { label: string; color: string; sigla: string; absence: boolean }
> = {
    ESCALADO: { label: 'Escalado', color: '#16A34A', sigla: '', absence: false },
    FOLGA: { label: 'Folga', color: '#EBEEF2', sigla: 'F', absence: false },
    FALTA: { label: 'Falta', color: '#E32027', sigla: 'FT', absence: true },
    ATESTADO: { label: 'Atestado', color: '#F59E0B', sigla: 'AT', absence: true },
    FERIAS: { label: 'Férias', color: '#7C3AED', sigla: 'FE', absence: true },
    LICENCA: { label: 'Licença', color: '#0F766E', sigla: 'LI', absence: true },
    EXTRA: { label: 'Hora extra', color: '#16A34A', sigla: 'HE', absence: false },
    TREINAMENTO: { label: 'Treinamento', color: '#0EA5E9', sigla: 'TR', absence: true },
};
